import { ASSESSMENT_TYPES } from './actions'

const initialState = {
  loading: false,
  error: null,
  currentAssessment: {
    assessment_id: '',
    assessment_set_id: '',
    assessment_assigned_id: '',
    instructions: '',
    questions: null,
    allowProctoring: false,
    title: '',
    type: '',
    isPractice: false,
    responses: {},
  },

  activeAssessments: {
    loading: false,
    error: null,
    data: [],
  },

  completedAssessments: {
    loading: false,
    error: null,
    data: [],
  },

  practiceAssessments: {
    history: [],
    loading: false,
    error: null,
    pagination: null,
    currentFilter: null, // Track current assessment type filter
  },

  // Add separate state for different assessment types
  communicationPracticeAssessments: {
    history: [],
    loading: false,
    error: null,
    pagination: null,
  },

  aptitudePracticeAssessments: {
    history: [],
    loading: false,
    error: null,
    pagination: null,
  },

  // Add completed practice assessments state
  completedPracticeAssessments: {
    data: [],
    pagination: null,
    loading: false,
    error: null,
  },

  // PDF report generation state
  pdfReport: {
    loading: false,
    error: null,
    lastGenerated: null,
  },

  activeAssessmentsFilters: {
    duration: [],
    assessmentType: [],
    search: '',
  },

  completedAssessmentsFilters: {
    timeFilter: [],
    assessmentType: [],
    search: '',
  },

  reports: {
    data: [],
    loading: false,
    error: null,
  },

  // Merged PlacementPrep state
  practiceAccess: {
    hasAccess: false, // Overall access - true if ANY type has access
    loading: false,
    error: null,
    Communication: {
      hasAccess: false,
      loading: false,
      error: null,
      completedCount: 0,
      requiredCount: 2,
      reason: 'Complete 2 Communication assessments to unlock practice',
    },
    Aptitude: {
      hasAccess: false,
      loading: false,
      error: null,
      completedCount: 0,
      requiredCount: 2,
      reason: 'Complete 2 Aptitude assessments to unlock practice',
    },
  },

  practiceScores: {
    data: [],
    dayZero: null,
    loading: false,
    error: null,
  },

  // Add aptitude practice scores state
  aptitudePracticeScores: {
    data: [],
    dayZero: null,
    loading: false,
    error: null,
  },

  savingResponse: false,
  savingResponseError: null,

  retakeEligibility: {
    data: null,
    loading: false,
    error: null,
  },

  // Activity map state
  activityData: [],
  activityLoading: false,
  activityError: null,
  activityDateRange: {
    startDate: null,
    endDate: null,
  },

  timeSpentPerQuestion: {}, // { [questionId]: timeInSeconds }
  activeQuestionTimer: null, // Currently active question ID

  // NEW: Solutions state
  solutions: {
    data: null,
    loading: false,
    error: null,
  },

  // NEW: Aptitude score state
  aptitudeScore: {
    data: null,
    loading: false,
    error: null,
  },

  dropoutAssessments: {
    loading: false,
    error: null,
    data: [],
    search: '',
  },
  dropoutAssessmentsFilters: {
    timeFilter: [],
    assessmentType: [],
    search: '',
  },

  diagnosisStatus: {
    loading: false,
    error: null,
    data: {
      communication: { completed: 0, required: 2 },
      aptitude: { completed: 0, required: 2 }
    }
  },

  // Add student corporate status state
  studentCorporateStatus: {
    loading: false,
    isCorporate: false,
    error: null,
  },
  directAssessment: {
    loading: false,
    error: null,
    assessment: null,
  },

  behaviourSpecialisations: {
    loading: false,
    error: null,
    data: [],
    mappedIds: [],
    savedIds: [],
  },
}

const assessmentReducer = (state = initialState, action) => {
  switch (action.type) {
    case ASSESSMENT_TYPES.FETCH_QUESTIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      }

    case ASSESSMENT_TYPES.FETCH_QUESTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        currentAssessment: {
          ...state.currentAssessment,
          questions: action.payload.data,
        },
      }

    case ASSESSMENT_TYPES.FETCH_QUESTIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      }

    case ASSESSMENT_TYPES.SET_CURRENT_ASSESSMENT_ID:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          assessment_id: action.payload.assessment_id,
          assessment_set_id: action.payload.assessment_set_id,
          assessment_assigned_id: action.payload.assessment_assigned_id,
          allowProctoring: action.payload.allowProctoring,
          allowVerification: action.payload.allowVerification,
          studentId: action.payload.studentId,
          title: action.payload.title,
          type: action.payload.type,
          isPractice: action.payload.isPractice,
          isCorporate: action.payload.isCorporate,
          responses: state.currentAssessment.responses || {},
          questions: state.currentAssessment.questions || null,
          AptitudeDiagnosisRemain: action.payload.AptitudeDiagnosisRemain,
          CommunicationDiagnosisRemain: action.payload.CommunicationDiagnosisRemain,
        },
      }

    case ASSESSMENT_TYPES.SAVE_QUESTION_RESPONSE:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            [action.payload.questionId]: action.payload.answer,
          },
        },
      }

    case ASSESSMENT_TYPES.FETCH_ACTIVE_ASSESSMENTS_REQUEST:
      return {
        ...state,
        activeAssessments: {
          ...state.activeAssessments,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_ACTIVE_ASSESSMENTS_SUCCESS:
      return {
        ...state,
        activeAssessments: {
          loading: false,
          error: null,
          data: Array.isArray(action.payload)
            ? action.payload
            : Array.isArray(action.payload?.data)
            ? action.payload.data
            : [],
        },
      }

    case ASSESSMENT_TYPES.FETCH_ACTIVE_ASSESSMENTS_FAILURE:
      return {
        ...state,
        activeAssessments: {
          ...state.activeAssessments,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_COMPLETED_ASSESSMENTS_REQUEST:
      return {
        ...state,
        completedAssessments: {
          ...state.completedAssessments,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_COMPLETED_ASSESSMENTS_SUCCESS:
      return {
        ...state,
        completedAssessments: {
          loading: false,
          error: null,
          data: Array.isArray(action.payload)
            ? action.payload
            : Array.isArray(action.payload?.data)
            ? action.payload.data
            : [],
        },
      }

    case ASSESSMENT_TYPES.FETCH_COMPLETED_ASSESSMENTS_FAILURE:
      return {
        ...state,
        completedAssessments: {
          ...state.completedAssessments,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_HISTORY_REQUEST:
      return {
        ...state,
        practiceAssessments: {
          ...state.practiceAssessments,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_HISTORY_SUCCESS:
      const newAssessments = Array.isArray(action.payload?.assessments)
        ? action.payload.assessments
        : Array.isArray(action.payload)
        ? action.payload
        : []
      const existingAssessments = state.practiceAssessments.history || []
      const assessmentType = action.payload?.assessmentType

      // If it's page 1 or fresh load, replace the data. Otherwise, append.
      const isFirstPage = action.payload?.pagination?.currentPage === 1
      let finalAssessments = isFirstPage
        ? newAssessments
        : [...existingAssessments, ...newAssessments]

      // ENHANCED: Additional filtering to ensure type consistency
      if (assessmentType) {
        finalAssessments = finalAssessments.filter(assessment => {
          const type = (
            assessment.type ||
            assessment.assessmentType ||
            ''
          ).toLowerCase()
          return type === assessmentType.toLowerCase()
        })
      }

      return {
        ...state,
        practiceAssessments: {
          history: finalAssessments,
          loading: false,
          error: null,
          pagination: action.payload?.pagination || null,
          currentFilter: assessmentType,
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_HISTORY_FAILURE:
      return {
        ...state,
        practiceAssessments: {
          ...state.practiceAssessments,
          loading: false,
          error: action.payload,
        },
      }

    // PDF report generation cases
    case ASSESSMENT_TYPES.GENERATE_PDF_REPORT_REQUEST:
      return {
        ...state,
        pdfReport: {
          ...state.pdfReport,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.GENERATE_PDF_REPORT_SUCCESS:
      return {
        ...state,
        pdfReport: {
          loading: false,
          error: null,
          lastGenerated: action.payload,
        },
      }

    case ASSESSMENT_TYPES.GENERATE_PDF_REPORT_FAILURE:
      return {
        ...state,
        pdfReport: {
          ...state.pdfReport,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.SET_ACTIVE_ASSESSMENT_FILTERS:
      return {
        ...state,
        activeAssessmentsFilters: action.payload,
      }

    case ASSESSMENT_TYPES.SET_COMPLETED_ASSESSMENT_FILTERS:
      return {
        ...state,
        completedAssessmentsFilters: action.payload,
      }

    case ASSESSMENT_TYPES.SET_CURRENT_ASSESSMENT_INSTRUCTIONS:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          instructions: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_REPORTS_REQUEST:
      return {
        ...state,
        reports: {
          ...state.reports,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_REPORTS_SUCCESS:
      return {
        ...state,
        reports: {
          data: Array.isArray(action.payload) ? action.payload : [],
          loading: false,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_REPORTS_FAILURE:
      return {
        ...state,
        reports: {
          ...state.reports,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.RESET_RESPONSES:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {},
        },
        timeSpentPerQuestion: {},
        activeQuestionTimer: null,
      }

    case ASSESSMENT_TYPES.SUBMIT_ASSESSMENT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      }

    case ASSESSMENT_TYPES.SUBMIT_ASSESSMENT_SUCCESS:
      return {
        ...state,
        loading: false,
        // FIXED: Don't reset the entire currentAssessment, preserve it for completion
        // Just mark it as submitted
        currentAssessment: {
          ...state.currentAssessment,
          isSubmitted: true, // Add submission flag
        },
        error: null,
        // FIXED: Don't reset responses here, keep them for completion display
      }

    case ASSESSMENT_TYPES.SUBMIT_ASSESSMENT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      }

    case ASSESSMENT_TYPES.SAVE_RESPONSE_REQUEST:
      return {
        ...state,
        savingResponse: true,
        savingResponseError: null,
      }

    // FIXED: Improved response saving with proper structure
case ASSESSMENT_TYPES.SAVE_RESPONSE_SUCCESS: {
  const { questionId, answer } = action.payload;
  const { subQuestionId, questionType } = action;

  console.log('🔍 [REDUCER] Saving response:', { questionId, answer, subQuestionId, questionType });

  // FIXED: Differentiate between question types
  if (questionType === 'questionBasedResponse' || questionType === 'emailWriting') {
    // SIMPLE FORMAT: Just store { questionId, answer }
    const currentSectionResponses = state.currentAssessment.responses[questionType] || [];
    const existingIndex = currentSectionResponses.findIndex(r => r.questionId === questionId);

    const updatedSectionResponses = existingIndex >= 0
      ? currentSectionResponses.map((item, idx) => 
          idx === existingIndex ? { questionId, answer } : item
        )
      : [...currentSectionResponses, { questionId, answer }];

    return {
      ...state,
      savingResponse: false,
      currentAssessment: {
        ...state.currentAssessment,
        responses: {
          ...state.currentAssessment.responses,
          [questionType]: updatedSectionResponses,
        },
      },
    };
  } 
  else if (questionType === 'paragraphReading' || questionType === 'audioQuestion') {
    // MCQ FORMAT: Store { questionId, responses: [{ subQuestionId, selectedOption }] }
    const currentSectionResponses = state.currentAssessment.responses[questionType] || [];
    const existingQuestionIndex = currentSectionResponses.findIndex(
      q => q.questionId === questionId
    );

    if (existingQuestionIndex >= 0) {
      // Update existing question
      const updatedSectionResponses = [...currentSectionResponses];
      const existingQuestion = updatedSectionResponses[existingQuestionIndex];

      // Find existing subQuestion response
      const existingResponseIndex = existingQuestion.responses.findIndex(
        r => r.subQuestionId === subQuestionId
      );

      if (existingResponseIndex >= 0) {
        // Update existing response
        existingQuestion.responses[existingResponseIndex].selectedOption = answer;
      } else {
        // Add new response
        existingQuestion.responses.push({
          subQuestionId,
          selectedOption: answer,
        });
      }

      return {
        ...state,
        savingResponse: false,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            [questionType]: updatedSectionResponses,
          },
        },
      };
    } else {
      // Add new question entry
      return {
        ...state,
        savingResponse: false,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            [questionType]: [
              ...currentSectionResponses,
              {
                questionId,
                responses: [
                  {
                    subQuestionId,
                    selectedOption: answer,
                  },
                ],
              },
            ],
          },
        },
      };
    }
  }
  else if (questionType === 'dictation' || questionType === 'sentenceCompletion' || questionType === 'sentenceBuild') {
    // ANSWERS OBJECT FORMAT: Store { questionId, answers: { subQuestionId: answer } }
    const currentSectionResponses = state.currentAssessment.responses[questionType] || [];
    const existingQuestionIndex = currentSectionResponses.findIndex(
      r => r.questionId === questionId
    );

    if (existingQuestionIndex >= 0) {
      const updatedSectionResponses = [...currentSectionResponses];
      const existingQuestion = updatedSectionResponses[existingQuestionIndex];
      
      if (!existingQuestion.answers) {
        existingQuestion.answers = {};
      }
      existingQuestion.answers[subQuestionId] = answer;

      return {
        ...state,
        savingResponse: false,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            [questionType]: updatedSectionResponses,
          },
        },
      };
    } else {
      return {
        ...state,
        savingResponse: false,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            [questionType]: [
              ...currentSectionResponses,
              {
                questionId,
                answers: {
                  [subQuestionId]: answer,
                },
              },
            ],
          },
        },
      };
    }
  }
  else if (!questionType) {
    // BEHAVIOR ASSESSMENT: Simple question:answer format
    return {
      ...state,
      savingResponse: false,
      currentAssessment: {
        ...state.currentAssessment,
        responses: {
          ...state.currentAssessment.responses,
          [questionId]: answer,
        },
      },
    };
  }

  // Default fallback
  return {
    ...state,
    savingResponse: false,
  };
}

    case ASSESSMENT_TYPES.SAVE_RESPONSE_FAILURE:
      return {
        ...state,
        savingResponse: false,
        saveResponseError: action.payload,
      }

    // FIXED: Improved video response storage
    case ASSESSMENT_TYPES.STORE_VIDEO_RESPONSE:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            videoResponse: [
              {
                questionId: action.payload.questionId,
                videoFileUrl: action.payload.parUrl,
                objectKey: action.payload.baseFilename,
              },
            ],
          },
        },
      }

    // FIXED: Improved audio response storage
    case ASSESSMENT_TYPES.STORE_AUDIO_RESPONSE: {
      const currentParagraphReading =
        state.currentAssessment.responses.paragraphReading || []

      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          responses: {
            ...state.currentAssessment.responses,
            paragraphReading:
              currentParagraphReading.length > 0
                ? currentParagraphReading.map(response => ({
                    ...response,
                    audioUrl: action.payload.parUrl,
                    objectKey: action.payload.baseFilename,
                  }))
                : [
                    {
                      questionId:
                        action.payload.questionId ||
                        state.currentAssessment.questions?.communications?.[0]
                          ?.questionId,
                      audioUrl: action.payload.parUrl,
                      objectKey: action.payload.baseFilename,
                      responses: [],
                    },
                  ],
          },
        },
      }
    }

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_REQUEST:
      return {
        ...state,
        practiceAccess: {
          ...state.practiceAccess,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_SUCCESS:
      console.log('🔍 REDUCER: CHECK_PRACTICE_ACCESS_SUCCESS')
      console.log('🔍 REDUCER: Payload received:', action.payload)
      console.log(
        '🔍 REDUCER: Previous practice access state:',
        state.practiceAccess
      )

      const updatedPracticeAccessState = {
        ...action.payload,
        loading: false,
        error: null,
      }

      console.log(
        '🔍 REDUCER: New practice access state being set:',
        updatedPracticeAccessState
      )

      const updatedState = {
        ...state,
        practiceAccess: updatedPracticeAccessState,
      }

      console.log('🔍 REDUCER: Complete new state:', updatedState)

      return updatedState

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_FAILURE:
      return {
        ...state,
        practiceAccess: {
          ...state.practiceAccess,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.CHECK_RETAKE_ELIGIBILITY_REQUEST:
      return {
        ...state,
        retakeEligibility: {
          ...state.retakeEligibility,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.CHECK_RETAKE_ELIGIBILITY_SUCCESS:
      return {
        ...state,
        retakeEligibility: {
          data: action.payload,
          loading: false,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.CHECK_RETAKE_ELIGIBILITY_FAILURE:
      return {
        ...state,
        retakeEligibility: {
          ...state.retakeEligibility,
          loading: false,
          error: action.payload,
        },
      }

    // Activity map cases
    case ASSESSMENT_TYPES.FETCH_ACTIVITY_MAP_REQUEST:
      return {
        ...state,
        activityLoading: true,
        activityError: null,
      }

    case ASSESSMENT_TYPES.FETCH_ACTIVITY_MAP_SUCCESS:
      return {
        ...state,
        activityLoading: false,
        activityData: action.payload,
        activityError: null,
      }

    case ASSESSMENT_TYPES.FETCH_ACTIVITY_MAP_FAILURE:
      return {
        ...state,
        activityLoading: false,
        activityError: action.payload,
        activityData: [],
      }

    case ASSESSMENT_TYPES.SET_ACTIVITY_DATE_RANGE:
      return {
        ...state,
        activityDateRange: action.payload,
      }

    // Add completed practice assessments cases
    case ASSESSMENT_TYPES.FETCH_COMPLETED_PRACTICE_ASSESSMENTS_REQUEST:
      return {
        ...state,
        completedPracticeAssessments: {
          ...state.completedPracticeAssessments,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_COMPLETED_PRACTICE_ASSESSMENTS_SUCCESS:
      const newAssessmentsData =
        action.payload.assessments || action.payload.data || []
      const existingAssessmentsData =
        state.completedPracticeAssessments.data || []

      // If it's page 1 or fresh load, replace the data. Otherwise, append.
      const isFirstPageCompleted = action.payload.pagination?.currentPage === 1
      const finalAssessmentsData = isFirstPageCompleted
        ? newAssessmentsData
        : [...existingAssessmentsData, ...newAssessmentsData]

      console.log('🔍 REDUCER: Practice assessments success:', {
        newCount: newAssessmentsData.length,
        existingCount: existingAssessmentsData.length,
        finalCount: finalAssessmentsData.length,
        isFirstPage: isFirstPageCompleted,
        sampleData: finalAssessmentsData[0] || 'No data',
      })

      return {
        ...state,
        completedPracticeAssessments: {
          loading: false,
          error: null,
          data: finalAssessmentsData,
          pagination: action.payload.pagination || null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_COMPLETED_PRACTICE_ASSESSMENTS_FAILURE:
      return {
        ...state,
        completedPracticeAssessments: {
          ...state.completedPracticeAssessments,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.INIT_QUESTION_TIME:
      return {
        ...state,
        timeSpentPerQuestion: {
          ...state.timeSpentPerQuestion,
          [action.payload.questionId]: 0, // Initialize to 0
        },
        activeQuestionTimer: action.payload.questionId,
      }

    case ASSESSMENT_TYPES.TICK_QUESTION_TIME:
      // Only increment if this question is the active timer
      if (state.activeQuestionTimer === action.payload.questionId) {
        return {
          ...state,
          timeSpentPerQuestion: {
            ...state.timeSpentPerQuestion,
            [action.payload.questionId]:
              (state.timeSpentPerQuestion[action.payload.questionId] || 0) + 1,
          },
        }
      }
      return state

    case ASSESSMENT_TYPES.PAUSE_QUESTION_TIME:
      return {
        ...state,
        activeQuestionTimer: null,
      }

    case ASSESSMENT_TYPES.RESET_QUESTION_TIME:
      return {
        ...state,
        timeSpentPerQuestion: {
          ...state.timeSpentPerQuestion,
          [action.payload.questionId]: 0,
        },
      }

    case ASSESSMENT_TYPES.COMPLETE_ASSESSMENT:
      return {
        ...state,
        currentAssessment: {
          ...state.currentAssessment,
          isCompleted: true,
          completionData: action.payload,
        },
      }

    case ASSESSMENT_TYPES.RESET_ASSESSMENT_STATE:
      return {
        ...state,
        currentAssessment: {
          ...initialState.currentAssessment,
        },
        timeSpentPerQuestion: {},
        activeQuestionTimer: null,
      }

    // NEW: Solutions cases
    case ASSESSMENT_TYPES.FETCH_SOLUTIONS_REQUEST:
      return {
        ...state,
        solutions: {
          ...state.solutions,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_SOLUTIONS_SUCCESS:
      return {
        ...state,
        solutions: {
          data: action.payload,
          loading: false,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_SOLUTIONS_FAILURE:
      return {
        ...state,
        solutions: {
          ...state.solutions,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_SCORE_REQUEST:
      return {
        ...state,
        aptitudeScore: {
          ...state.aptitudeScore,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_SCORE_SUCCESS:
      return {
        ...state,
        aptitudeScore: {
          data: action.payload,
          loading: false,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_SCORE_FAILURE:
      return {
        ...state,
        aptitudeScore: {
          ...state.aptitudeScore,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_REQUEST:
      console.log('🔍 REDUCER: CHECK_PRACTICE_ACCESS_REQUEST')
      return {
        ...state,
        practiceAccess: {
          ...state.practiceAccess,
          loading: true,
          error: null,
          Communication: {
            ...state.practiceAccess.Communication,
            loading: true,
            error: null,
          },
          Aptitude: {
            ...state.practiceAccess.Aptitude,
            loading: true,
            error: null,
          },
        },
      }

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_SUCCESS:
      console.log('🔍 REDUCER: CHECK_PRACTICE_ACCESS_SUCCESS')
      console.log('🔍 REDUCER: Payload received:', action.payload)
      console.log(
        '🔍 REDUCER: Previous practice access state:',
        state.practiceAccess
      )

      const newPracticeAccessState = {
        ...action.payload,
        loading: false,
        error: null,
      }

      console.log(
        '🔍 REDUCER: New practice access state being set:',
        newPracticeAccessState
      )

      const newState = {
        ...state,
        practiceAccess: newPracticeAccessState,
      }

      console.log('🔍 REDUCER: Complete new state:', newState)

      return newState

    case ASSESSMENT_TYPES.CHECK_PRACTICE_ACCESS_FAILURE:
      console.log('🔍 REDUCER: CHECK_PRACTICE_ACCESS_FAILURE')
      console.log('🔍 REDUCER: Error payload:', action.payload)

      return {
        ...state,
        practiceAccess: {
          ...state.practiceAccess,
          hasAccess: false,
          loading: false,
          error: action.payload.error || action.payload,
          Communication: {
            ...state.practiceAccess.Communication,
            hasAccess: false,
            loading: false,
            error: action.payload.error || action.payload,
          },
          Aptitude: {
            ...state.practiceAccess.Aptitude,
            hasAccess: false,
            loading: false,
            error: action.payload.error || action.payload,
          },
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_SCORES_REQUEST:
      return {
        ...state,
        practiceScores: {
          ...state.practiceScores,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_SCORES_SUCCESS:
      return {
        ...state,
        practiceScores: {
          loading: false,
          error: null,
          data: action.payload.data || action.payload,
          dayZero: action.payload.dayZero || null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_PRACTICE_SCORES_FAILURE:
      return {
        ...state,
        practiceScores: {
          ...state.practiceScores,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_PRACTICE_SCORES_REQUEST:
      return {
        ...state,
        aptitudePracticeScores: {
          ...state.aptitudePracticeScores,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_PRACTICE_SCORES_SUCCESS:
      return {
        ...state,
        aptitudePracticeScores: {
          loading: false,
          error: null,
          data: action.payload.data || action.payload,
          dayZero: action.payload.dayZero || null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_APTITUDE_PRACTICE_SCORES_FAILURE:
      return {
        ...state,
        aptitudePracticeScores: {
          ...state.aptitudePracticeScores,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_DROPOUT_ASSESSMENTS_REQUEST:
      return {
        ...state,
        dropoutAssessments: {
          ...state.dropoutAssessments,
          loading: true,
        },
      }

    case ASSESSMENT_TYPES.FETCH_DROPOUT_ASSESSMENTS_SUCCESS:
      console.log(
        '🔧 Reducer: FETCH_DROPOUT_ASSESSMENTS_SUCCESS payload:',
        action.payload
      )
      return {
        ...state,
        dropoutAssessments: {
          loading: false,
          error: null,
          data: action.payload.data,
        },
      }

    case ASSESSMENT_TYPES.FETCH_DROPOUT_ASSESSMENTS_FAILURE:
      return {
        ...state,
        dropoutAssessments: {
          ...state.dropoutAssessments,
          loading: false,
          error: action.payload,
        },
      }

    case ASSESSMENT_TYPES.SET_DROPOUT_ASSESSMENT_FILTERS:
      return {
        ...state,
        dropoutAssessmentsFilters: action.payload,
      }

    case ASSESSMENT_TYPES.FETCH_DIAGNOSIS_STATUS_REQUEST:
      return {
        ...state,
        diagnosisStatus: {
          ...state.diagnosisStatus,
          loading: true,
          error: null,
        },
      }

    case ASSESSMENT_TYPES.FETCH_DIAGNOSIS_STATUS_SUCCESS:
      return {
        ...state,
        diagnosisStatus: {
          loading: false,
          error: null,
          data: action.payload,
        },
      }

    case ASSESSMENT_TYPES.FETCH_DIAGNOSIS_STATUS_FAILURE:
      return {
        ...state,
        diagnosisStatus: {
          ...state.diagnosisStatus,
          loading: false,
          error: action.payload,
        },
      }

    // NEW: Student corporate status cases
    case ASSESSMENT_TYPES.CHECK_STUDENT_CORPORATE_STATUS_REQUEST:
      return {
        ...state,
        studentCorporateStatus: {
          ...state.studentCorporateStatus,
          loading: true,
          error: null,
        },
      }
    
    case ASSESSMENT_TYPES.CHECK_STUDENT_CORPORATE_STATUS_SUCCESS:
      return {
        ...state,
        studentCorporateStatus: {
          loading: false,
          isCorporate: action.payload.isCorporate,
          error: null,
        },
      }
    
    case ASSESSMENT_TYPES.CHECK_STUDENT_CORPORATE_STATUS_FAILURE:
      return {
        ...state,
        studentCorporateStatus: {
          ...state.studentCorporateStatus,
          loading: false,
          error: action.payload,
        },
      }
    
    // Direct Assessment Cases
    case ASSESSMENT_TYPES.LOAD_DIRECT_ASSESSMENT_REQUEST:
      return {
        ...state,
        directAssessment: {
          ...state.directAssessment,
          loading: true,
          error: null,
        },
      }
    
    case ASSESSMENT_TYPES.LOAD_DIRECT_ASSESSMENT_SUCCESS:
      return {
        ...state,
        directAssessment: {
          loading: false,
          error: null,
          assessment: action.payload,
        },
      }
    
    case ASSESSMENT_TYPES.LOAD_DIRECT_ASSESSMENT_FAILURE:
      return {
        ...state,
        directAssessment: {
          ...state.directAssessment,
          loading: false,
          error: action.payload,
        },
      }
    
    // Behaviour specialisations
    case ASSESSMENT_TYPES.FETCH_BEHAVIOUR_SPECIALISATIONS_REQUEST:
      return {
        ...state,
        behaviourSpecialisations: { loading: true, error: null, data: [], mappedIds: [], savedIds: [] },
      }
    case ASSESSMENT_TYPES.FETCH_BEHAVIOUR_SPECIALISATIONS_SUCCESS:
      return {
        ...state,
        behaviourSpecialisations: {
          loading: false,
          error: null,
          data: action.payload?.specialisations || [],
          mappedIds: action.payload?.mappedSpecialisationIds || [],
          savedIds: action.payload?.savedSpecialisationIds || [],
        },
      }
    case ASSESSMENT_TYPES.FETCH_BEHAVIOUR_SPECIALISATIONS_FAILURE:
      return {
        ...state,
        behaviourSpecialisations: { loading: false, error: action.payload, data: [], mappedIds: [], savedIds: [] },
      }

    default:
      return state
  }
}

export default assessmentReducer
